<template>
  <v-card outlined class="career-panel">
    <v-card-title class="career-panel__title">
      <div>
        <span class="career-panel__heading">Plano de carreira — Associados</span>
      </div>
      <v-tooltip bottom max-width="320">
        <template v-slot:activator="{ on, attrs }">
          <v-icon small class="help-icon" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
        </template>
        Metas numéricas usam empresas (parceiros), associados e faturamento do período e contexto
        selecionados no dashboard. Marcos e bonificações oficiais seguem contrato e regras de
        sustentabilidade (ex.: recorrência em empresas premium ativas).
      </v-tooltip>
    </v-card-title>

    <v-card-text class="career-panel__body pt-0">
      <div class="career-panel__current">
        <v-chip class="career-panel__chip" color="#EEF3FF" text-color="#24305E">
          <v-icon left small color="#24A859">mdi-trophy-outline</v-icon>
          Seu nível: {{ careerState.currentLevel.title }}
        </v-chip>
        <p v-if="!careerState.isMaxLevel" class="career-panel__next-hint">
          Próximo:
          <strong>{{ careerState.nextLevelTitle }}</strong>
        </p>
        <p v-else class="career-panel__next-hint career-panel__next-hint--max">
          <v-icon small color="#24A859" class="mr-1">mdi-check-decagram</v-icon>
          Você atingiu o nível máximo do plano exibido. Continue expandindo a rede.
        </p>
      </div>

      <v-alert dense outlined type="info" color="#24305E" class="career-panel__sustain mb-4">
        <strong>Sustentabilidade:</strong> recorrência válida para empresas premium em transação;
        foco em faturamento real — conforme documento oficial.
      </v-alert>

      <div v-if="careerState.nextSteps.length" class="career-panel__progress-block mb-4">
        <div class="career-panel__progress-head">
          Progresso para o próximo nível
          <span class="career-panel__progress-pct">{{ overallProgress }}%</span>
        </div>
        <v-progress-linear
          :value="overallProgress"
          color="#24A859"
          rounded
          height="8"
          class="mb-3"
        />
        <div
          v-for="step in careerState.nextSteps"
          :key="step.id"
          class="career-panel__metric"
        >
          <div class="career-panel__metric-label">{{ step.label }}</div>
          <div class="career-panel__metric-row">
            <span class="career-panel__metric-values">
              {{ formatStepValue(step.unit, step.current) }} /
              {{ formatStepValue(step.unit, step.target) }}
            </span>
            <span class="career-panel__metric-pct">{{ stepPercent(step) }}%</span>
          </div>
          <v-progress-linear
            :value="stepPercent(step)"
            color="#242968"
            background-color="rgba(36,41,104,0.08)"
            rounded
            height="6"
            class="mt-1"
          />
        </div>
      </div>

      <div class="career-panel__cta-wrap">
        <v-btn
          color="#242968"
          dark
          depressed
          rounded
          class="career-panel__cta"
          @click="levelsDialog = true"
        >
          <v-icon left small>mdi-stairs</v-icon>
          Conheça os 4 níveis estratégicos
        </v-btn>
      </div>

      <p class="career-panel__tagline">
        Aqui você constrói uma renda crescente baseada no consumo real.
      </p>
    </v-card-text>

    <v-dialog
      v-model="levelsDialog"
      max-width="920"
      scrollable
      content-class="career-levels-dialog"
    >
      <v-card class="career-levels-dialog__card">
        <v-card-title class="career-levels-dialog__head">
          <div>
            <div class="career-levels-dialog__title">Plano de carreira — 4 níveis estratégicos</div>
            <div class="career-levels-dialog__subtitle">BRPoints · Associados</div>
          </div>
          <v-btn icon aria-label="Fechar" @click="levelsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="career-levels-dialog__body">
          <section class="career-levels-dialog__snapshot">
            <h3 class="career-levels-dialog__snapshot-title">O que você já conquistou até agora</h3>
            <p class="career-levels-dialog__snapshot-hint">
              Valores conforme o período e filtros do dashboard acima.
            </p>
            <div class="career-levels-dialog__snapshot-grid">
              <div class="career-levels-dialog__snapshot-item">
                <span class="career-levels-dialog__snapshot-label">Empresas (parceiros)</span>
                <span class="career-levels-dialog__snapshot-value">{{ snapshot.empresas }}</span>
              </div>
              <div class="career-levels-dialog__snapshot-item">
                <span class="career-levels-dialog__snapshot-label">Associados na rede</span>
                <span class="career-levels-dialog__snapshot-value">{{ snapshot.associados }}</span>
              </div>
              <div class="career-levels-dialog__snapshot-item career-levels-dialog__snapshot-item--wide">
                <span class="career-levels-dialog__snapshot-label">Volume transacionado</span>
                <span class="career-levels-dialog__snapshot-value">{{
                  formatStepValue("currency", snapshot.faturamento)
                }}</span>
              </div>
            </div>
          </section>

          <section
            v-for="lvl in levels"
            :key="lvl.rank"
            class="career-levels-dialog__level"
            :class="{
              'career-levels-dialog__level--done': lvl.rank < careerState.currentRank,
              'career-levels-dialog__level--current': lvl.rank === careerState.currentRank,
            }"
          >
            <div class="career-levels-dialog__level-top">
              <div class="career-levels-dialog__level-heading">
                <v-icon
                  class="career-levels-dialog__level-icon"
                  :color="lvl.rank <= careerState.currentRank ? '#24A859' : '#9aa3c2'"
                >
                  {{
                    lvl.rank <= careerState.currentRank ? "mdi-check-decagram" : "mdi-circle-outline"
                  }}
                </v-icon>
                <div>
                  <div class="career-levels-dialog__level-title">
                    Nível {{ lvl.rank }} — {{ lvl.title }}
                  </div>
                  <div class="career-levels-dialog__level-objective">{{ lvl.objective }}</div>
                </div>
              </div>
              <v-chip
                small
                class="career-levels-dialog__status-chip"
                :color="statusChipColor(levelStatus(lvl.rank))"
                :text-color="statusChipTextColor(levelStatus(lvl.rank))"
              >
                {{ statusLabel(levelStatus(lvl.rank)) }}
              </v-chip>
            </div>

            <div class="career-levels-dialog__progress-section">
              <div
                v-for="row in progressRowsFor(lvl.rank)"
                :key="lvl.rank + row.label"
                class="career-levels-dialog__prow"
              >
                <div class="career-levels-dialog__prow-head">
                  <span>{{ row.label }}</span>
                  <span class="career-levels-dialog__prow-values">
                    {{ formatStepValue(row.unit, row.current) }} /
                    {{ formatStepValue(row.unit, row.target) }}
                  </span>
                </div>
                <v-progress-linear
                  :value="rowPercent(row)"
                  color="#242968"
                  background-color="rgba(36,41,104,0.08)"
                  rounded
                  height="6"
                />
              </div>
            </div>

            <ul class="career-levels-dialog__list">
              <li>
                <strong>Metas</strong>
                <span>{{ lvl.metas.join(" · ") }}</span>
              </li>
              <li>
                <strong>Remuneração</strong>
                <span>{{ lvl.remuneracao.join(" ") }}</span>
              </li>
              <li>
                <strong>Recorrência</strong>
                <span>{{ lvl.recorrencia.join(" ") }}</span>
              </li>
              <li>
                <strong>Reconhecimento</strong>
                <span>{{ lvl.reconhecimento.join(" ") }}</span>
              </li>
              <li>
                <strong>Papel estratégico</strong>
                <span>{{ lvl.papel.join(" · ") }}</span>
              </li>
            </ul>
          </section>
        </v-card-text>

        <v-divider />

        <v-card-actions class="career-levels-dialog__actions">
          <v-spacer />
          <v-btn text color="#242968" @click="levelsDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { DashboardType } from "@/types/dashboard/Dashboard";
import {
  CAREER_LEVELS,
  computeCareerState,
  formatStepValue,
  overallNextProgressPercent,
  CareerState,
  NextStepMetric,
  getDashboardSnapshot,
  DashboardSnapshot,
  getProgressRowsForLevel,
  LevelProgressRow,
  getLevelUiStatus,
  LevelUiStatus,
  CareerRank,
} from "@/config/associateCareer";

export default defineComponent({
  name: "AssociateCareerPanel",

  props: {
    dashboard: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      levelsDialog: false,
      levels: CAREER_LEVELS,
    };
  },

  computed: {
    careerState(): CareerState {
      return computeCareerState(this.dashboard as DashboardType);
    },
    overallProgress(): number {
      return overallNextProgressPercent(this.careerState.nextSteps);
    },
    snapshot(): DashboardSnapshot {
      return getDashboardSnapshot(this.dashboard as DashboardType);
    },
  },

  methods: {
    formatStepValue,
    stepPercent(step: NextStepMetric): number {
      if (step.target <= 0) return 0;
      return Math.min(100, Math.round((step.current / step.target) * 1000) / 10);
    },
    progressRowsFor(rank: CareerRank): LevelProgressRow[] {
      return getProgressRowsForLevel(this.dashboard as DashboardType, rank);
    },
    rowPercent(row: LevelProgressRow): number {
      if (row.target <= 0) return 0;
      return Math.min(100, Math.round((row.current / row.target) * 1000) / 10);
    },
    levelStatus(levelRank: CareerRank): LevelUiStatus {
      return getLevelUiStatus(levelRank, this.careerState.currentRank);
    },
    statusLabel(s: LevelUiStatus): string {
      const map: Record<LevelUiStatus, string> = {
        conquistado: "Conquistado",
        atual: "Seu nível hoje",
        proximo: "Próximo objetivo",
        futuro: "A conquistar",
      };
      return map[s];
    },
    statusChipColor(s: LevelUiStatus): string {
      const map: Record<LevelUiStatus, string> = {
        conquistado: "#E8F5E9",
        atual: "#E3F2FD",
        proximo: "#FFF8E1",
        futuro: "#F5F5F5",
      };
      return map[s];
    },
    statusChipTextColor(s: LevelUiStatus): string {
      const map: Record<LevelUiStatus, string> = {
        conquistado: "#2E7D32",
        atual: "#1565C0",
        proximo: "#F57F17",
        futuro: "#616161",
      };
      return map[s];
    },
  },
});
</script>

<style scoped lang="scss">
.career-panel {
  border-radius: 14px !important;
  border-color: rgba(36, 41, 104, 0.12) !important;
}

.career-panel__title {
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
  padding: 14px 18px 8px !important;
  font-size: 1rem;
  font-weight: 700;
  color: #222744;
}

.career-panel__heading {
  display: block;
  font-size: 15px;
}

.career-panel__brand {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #e5183d;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-top: 2px;
}

.help-icon {
  color: #7d86a8 !important;
  cursor: help;
  margin-top: 4px;
}

.career-panel__body {
  padding-left: 18px !important;
  padding-right: 18px !important;
  padding-bottom: 16px !important;
}

.career-panel__current {
  margin-bottom: 12px;
}

.career-panel__chip {
  font-weight: 600 !important;
  border-radius: 10px !important;
}

.career-panel__next-hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #5c6478;
}

.career-panel__next-hint--max {
  color: #1f8a4d;
  font-weight: 600;
}

.career-panel__sustain {
  border-radius: 10px !important;
  font-size: 12px !important;
}

.career-panel__progress-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #222744;
  margin-bottom: 6px;
}

.career-panel__progress-pct {
  color: #24a859;
}

.career-panel__metric {
  margin-bottom: 12px;
}

.career-panel__metric-label {
  font-size: 12px;
  font-weight: 600;
  color: #4f5675;
  margin-bottom: 4px;
}

.career-panel__metric-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #1f243f;
}

.career-panel__metric-pct {
  color: #242968;
}

.career-panel__cta-wrap {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.career-panel__cta {
  text-transform: none;
  letter-spacing: 0.02em;
  font-weight: 600 !important;
}

.career-panel__tagline {
  margin: 16px 0 0;
  font-size: 13px;
  font-style: italic;
  color: #5c6478;
  text-align: center;
  line-height: 1.45;
}
</style>

<style lang="scss">
/* v-dialog renderiza fora do escopo scoped */
.career-levels-dialog__card {
  border-radius: 16px !important;
}

.career-levels-dialog__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px !important;
}

.career-levels-dialog__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f243f;
  line-height: 1.3;
}

.career-levels-dialog__subtitle {
  font-size: 12px;
  font-weight: 600;
  color: #e5183d;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 4px;
}

.career-levels-dialog__body {
  padding: 20px !important;
  max-height: 70vh;
}

.career-levels-dialog__snapshot {
  margin-bottom: 24px;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f5f7fb 100%);
  border-radius: 12px;
  border: 1px solid rgba(36, 41, 104, 0.1);
}

.career-levels-dialog__snapshot-title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: #222744;
}

.career-levels-dialog__snapshot-hint {
  margin: 0 0 14px;
  font-size: 12px;
  color: #6b7289;
}

.career-levels-dialog__snapshot-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (max-width: 600px) {
  .career-levels-dialog__snapshot-grid {
    grid-template-columns: 1fr;
  }
}

.career-levels-dialog__snapshot-item {
  background: #fff;
  border-radius: 10px;
  padding: 12px 14px;
  border: 1px solid rgba(36, 41, 104, 0.08);
}

.career-levels-dialog__snapshot-item--wide {
  grid-column: 1 / -1;
}

.career-levels-dialog__snapshot-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #6b7289;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.career-levels-dialog__snapshot-value {
  font-size: 20px;
  font-weight: 700;
  color: #242968;
}

.career-levels-dialog__level {
  border: 1px solid rgba(36, 41, 104, 0.12);
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 14px;
  background: #fff;
}

.career-levels-dialog__level--current {
  border-color: rgba(36, 168, 89, 0.45);
  box-shadow: 0 0 0 1px rgba(36, 168, 89, 0.12);
  background: rgba(247, 255, 252, 0.5);
}

.career-levels-dialog__level--done:not(.career-levels-dialog__level--current) {
  opacity: 0.96;
}

.career-levels-dialog__level-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.career-levels-dialog__level-heading {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.career-levels-dialog__level-icon {
  margin-top: 2px;
}

.career-levels-dialog__level-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f243f;
}

.career-levels-dialog__level-objective {
  font-size: 13px;
  color: #6b7289;
  margin-top: 4px;
  line-height: 1.4;
}

.career-levels-dialog__status-chip {
  font-weight: 600 !important;
  flex-shrink: 0;
}

.career-levels-dialog__progress-section {
  margin-bottom: 14px;
}

.career-levels-dialog__prow {
  margin-bottom: 10px;
}

.career-levels-dialog__prow-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #4f5675;
  margin-bottom: 4px;
}

.career-levels-dialog__prow-values {
  color: #242968;
  white-space: nowrap;
}

.career-levels-dialog__list {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  color: #4f5675;
  line-height: 1.5;

  li {
    margin-bottom: 10px;
  }

  strong {
    color: #222744;
    display: block;
    margin-bottom: 2px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
}

.career-levels-dialog__actions {
  padding: 12px 16px !important;
}
</style>
